import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { theme } from '../../styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MapFilters from './MapFilters';
import Timeline, { TIMELINE_ANIMATION, getTimelineAnimationDuration } from '../Timeline/Timeline';
import InfoBox from './InfoBox';
import { Global, css } from '@emotion/core';
import { MapContainer, MapScrollContainer, PinContainer, PinShape, PinCount, CountryLabel } from './MapPin.css';
import worldMap from '../../../static/world-map.json';

const geoUrl = worldMap;

// SVG path for map pin shape
const PIN_PATH = 'M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z';

// Animation timing variables (in ms)
const MAP_ANIMATION = {
  MAP_DELAY_AFTER_LINE: 300, // Start map fade during dots animation (after line + some dots)
  MAP_FADE_DURATION: 600,    // Sequence 3: map fade in duration
  PIN_STAGGER: 50,          // Sequence 4: delay between each pin
  PIN_DURATION: 500,         // Sequence 4: each pin animation duration
};

const WorldMap = ({
  selectedCountry,
  query,
  setSelectedCountry,
  setSelectedService,
  selectedService,
  allProjectsJson,
  allProjects,
  lang,
  selectedYear,
  setSelectedYear,
  filterMode,
  activateFilterMode,
  activateTimelineMode,
}) => {
  const [markers, setMarkers] = useState([]);
  const [animatingMarkers, setAnimatingMarkers] = useState([]);
  const [exitingMarkers, setExitingMarkers] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [infoBoxPosition, setInfoBoxPosition] = useState({ x: 0, y: 0 });
  // Animation sequence states
  const [timelineComplete, setTimelineComplete] = useState(false);
  const [timelineInitialStepsDone, setTimelineInitialStepsDone] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [pinsVisible, setPinsVisible] = useState(false);
  const [initialAnimationDone, setInitialAnimationDone] = useState(false);
  // Scroll hint state (mobile only)
  const [showScrollHint, setShowScrollHint] = useState(false);

  const mapContainerRef = useRef(null);
  const mapScrollRef = useRef(null);
  const scrollHintRef = useRef(null);
  const prevMarkersRef = useRef([]);
  const animationTimeoutRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Initialize showScrollHint based on isMobile
  useEffect(() => {
    setShowScrollHint(isMobile);
  }, [isMobile]);

  // Set initial horizontal scroll position at 30% on mobile
  useEffect(() => {
    if (isMobile && mapScrollRef.current && mapVisible) {
      const scrollWidth = mapScrollRef.current.scrollWidth;
      const clientWidth = mapScrollRef.current.clientWidth;
      const scrollableWidth = scrollWidth - clientWidth;
      mapScrollRef.current.scrollLeft = scrollableWidth * 0.3;
    }
  }, [isMobile, mapVisible]);

  // Handle scroll hint removal on mobile
  useEffect(() => {
    if (!isMobile || !showScrollHint) return;

    const hideScrollHint = () => {
      setShowScrollHint(false);
    };

    // Hide only after significant scroll interaction (e.g., after 5 seconds or substantial scroll distance)
    const scrollContainer = mapScrollRef.current;
    let scrollTimeout;
    if (scrollContainer) {
      scrollContainer.addEventListener('touchstart', () => {
        scrollTimeout = setTimeout(hideScrollHint, 5000);
      }, { once: true });
      scrollContainer.addEventListener('mousedown', () => {
        scrollTimeout = setTimeout(hideScrollHint, 5000);
      }, { once: true });
      scrollContainer.addEventListener('scroll', () => {
        const scrollLeft = scrollContainer.scrollLeft;
        const scrollWidth = scrollContainer.scrollWidth;
        const clientWidth = scrollContainer.clientWidth;
        if (scrollLeft > (scrollWidth - clientWidth) * 0.5 || scrollLeft < (scrollWidth - clientWidth) * 0.1) {
          hideScrollHint();
        }
      });
    }

    // Hide when scroll hint passes screen top (using Intersection Observer)
    const scrollHint = scrollHintRef.current;
    if (scrollHint) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // If hint is not intersecting and is above viewport, hide it
            if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
              hideScrollHint();
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(scrollHint);
      
      return () => {
        observer.disconnect();
        if (scrollContainer) {
          scrollContainer.removeEventListener('touchstart', hideScrollHint);
          scrollContainer.removeEventListener('mousedown', hideScrollHint);
          scrollContainer.removeEventListener('scroll', hideScrollHint);
          clearTimeout(scrollTimeout);
        }
      };
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('touchstart', hideScrollHint);
        scrollContainer.removeEventListener('mousedown', hideScrollHint);
        scrollContainer.removeEventListener('scroll', hideScrollHint);
        clearTimeout(scrollTimeout);
      }
    };
  }, [isMobile, showScrollHint]);

  // Calculate project counts per country
  const projectCountsByCountry = useMemo(() => {
    const counts = {};
    allProjectsJson.forEach(({ node: project }) => {
      if (project?.data?.countries) {
        project.data.countries.forEach(({ country }) => {
          counts[country] = (counts[country] || 0) + 1;
        });
      }
    });
    return counts;
  }, [allProjectsJson]);

  // Get projects for a specific country
  const getProjectsForCountry = (countryName) => {
    return allProjectsJson.filter(({ node: project }) =>
      project?.data?.countries?.some(({ country }) => country === countryName)
    );
  };

  // Animation duration in ms
  const ANIMATION_DURATION = MAP_ANIMATION.PIN_DURATION;
  const STAGGER_DELAY = MAP_ANIMATION.PIN_STAGGER;

  // Start map fade during timeline dots animation (not waiting for complete)
  // In filter mode, show map immediately since timeline is hidden
  useEffect(() => {
    if (filterMode === 'filters') {
      // In filter mode, show map immediately
      setMapVisible(true);
      setPinsVisible(true);
      setTimelineInitialStepsDone(true);
      setInitialAnimationDone(true);
    } else {
      // In timeline mode, animate as before
      const mapStartDelay = TIMELINE_ANIMATION.LINE_DURATION + MAP_ANIMATION.MAP_DELAY_AFTER_LINE;
      const mapTimer = setTimeout(() => {
        setMapVisible(true);
      }, mapStartDelay);
      
      return () => clearTimeout(mapTimer);
    }
  }, [filterMode]);

  // Handle timeline line animation complete - show pins as years are revealed
  const handleTimelineComplete = useCallback(() => {
    setTimelineComplete(true);
    setPinsVisible(true); // Enable pins - they will appear as selectedYear changes
  }, []);
  
  // Handle timeline initial year-stepping animation complete
  const handleTimelineInitialStepsComplete = useCallback(() => {
    setTimelineInitialStepsDone(true);
  }, []);

  // Mark initial animation done after pins finish animating
  useEffect(() => {
    if (pinsVisible && markers.length > 0 && !initialAnimationDone) {
      const totalPinAnimationTime = (markers.length * MAP_ANIMATION.PIN_STAGGER) + MAP_ANIMATION.PIN_DURATION;
      const timer = setTimeout(() => {
        setInitialAnimationDone(true);
      }, totalPinAnimationTime);
      return () => clearTimeout(timer);
    }
  }, [pinsVisible, markers.length, initialAnimationDone]);

  // Calculate new markers based on filters
  const calculateMarkers = useCallback(() => {
    let selectedFlags = query?.projects.flags_selector;

    // Filter by country
    if (selectedCountry) {
      selectedFlags = query?.projects?.flags_selector?.filter((flag) => {
        return flag.country === selectedCountry;
      });
    }

    // Filter by service
    if (selectedService) {
      selectedFlags = query?.projects?.flags_selector?.filter((flag) => {
        return allProjectsJson.find(({ node: project }) => {
          return project?.data?.countries.find((node) => {
            return node?.country === flag?.country;
          });
        });
      });
    }

    // Filter by year - show countries with projects from start until selected year
    // Skip year filter when a specific country or service filter is active (filter mode)
    if (selectedYear && !selectedCountry && !selectedService) {
      const countriesWithProjectsUpToYear = new Set();
      allProjects.forEach(({ node: project }) => {
        const projectYear = parseInt(project?.data?.year, 10);
        if (projectYear <= selectedYear) {
          if (project?.data?.countries) {
            project.data.countries.forEach(({ country }) => {
              countriesWithProjectsUpToYear.add(country);
            });
          }
        }
      });
      selectedFlags = selectedFlags.filter((flag) =>
        countriesWithProjectsUpToYear.has(flag.country)
      );
    }

    // If a country or service filter is active, return filtered results (even if empty)
    // Only show all countries when no filters are active and no results
    if (selectedFlags.length > 0) {
      return selectedFlags.map((selectedFlag) => ({
        markerOffset: -15,
        name: selectedFlag.country,
        coordinates: [selectedFlag.longitude, selectedFlag.latitude],
      }));
    } else if (selectedCountry || selectedService) {
      // Filter is active but no matches - return empty array instead of all countries
      return [];
    } else {
      return query.projects.flags_selector.map((flag) => ({
        markerOffset: -15,
        name: flag.country,
        coordinates: [flag.longitude, flag.latitude],
      }));
    }
  }, [selectedCountry, selectedService, selectedYear, allProjects, allProjectsJson, query.projects.flags_selector]);

  useEffect(() => {
    const newMarkers = calculateMarkers();
    const prevMarkerNames = new Set(prevMarkersRef.current.map(m => m.name));
    const newMarkerNames = new Set(newMarkers.map(m => m.name));

    // Find markers that are exiting (were in prev but not in new)
    const exiting = prevMarkersRef.current.filter(m => !newMarkerNames.has(m.name));
    
    // Find markers that are entering (in new but not in prev)
    const enteringNames = new Set(newMarkers.filter(m => !prevMarkerNames.has(m.name)).map(m => m.name));

    // Clear any existing animation timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // If there are exiting markers, animate them out first
    if (exiting.length > 0) {
      setExitingMarkers(exiting);
    }

    // Set new markers with animation state (as array to preserve order for staggered animation)
    const enteringArray = newMarkers.filter(m => enteringNames.has(m.name)).map(m => m.name);
    setAnimatingMarkers(enteringArray);
    setMarkers(newMarkers);

    // Clear animation states after all staggered animations complete
    const totalAnimationTime = ANIMATION_DURATION + (enteringArray.length * STAGGER_DELAY);
    animationTimeoutRef.current = setTimeout(() => {
      setExitingMarkers([]);
      setAnimatingMarkers([]);
    }, totalAnimationTime);

    // Update previous markers ref
    prevMarkersRef.current = newMarkers;

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [calculateMarkers]);

  // Close InfoBox when any filter changes
  useEffect(() => {
    setActiveMarker(null);
  }, [selectedCountry, selectedService, selectedYear]);

  // Handle click outside to close InfoBox
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!activeMarker) return;
      
      // Check if click is on the InfoBox or its children
      const infoBox = document.querySelector('[data-infobox]');
      if (infoBox && infoBox.contains(event.target)) return;
      
      // Check if click is on a marker
      const isMarkerClick = event.target.closest('.rsm-marker');
      if (isMarkerClick) return;
      
      setActiveMarker(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMarker]);

  const handleMarkerClick = (markerName, event) => {
    // Calculate position for InfoBox
    if (mapContainerRef.current && event) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      if (isMobile) {
        // On mobile: center horizontally, position below the pin
        setInfoBoxPosition({ x: 'center', y: y + 20 });
      } else {
        // On desktop: position to the right of click, or left if near edge
        const boxWidth = 300;
        const adjustedX = x + boxWidth > rect.width ? x - boxWidth - 20 : x + 20;
        setInfoBoxPosition({ x: adjustedX, y: Math.max(0, y - 50) });
      }
    }
    setActiveMarker(markerName);
  };

  const handleCloseInfoBox = () => {
    setActiveMarker(null);
  };

  // Pin size based on device - smaller default, bigger on hover/active
  const basePinScale = isMobile ? 0.5 : 0.30;
  const hoveredPinScale = isMobile ? 0.9 : 1;

  // Global styles for keyframe animations
  const globalStyles = css`
    @keyframes fadeInPin {
      from {
        opacity: 0;
        transform: scale(0.5);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    @keyframes fadeOutPin {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.5);
      }
    }
    @keyframes pulseOnHover {
      0% {
        transform: scale(1.2);
      }
      50% {
        transform: scale(1.4);
      }
      100% {
        transform: scale(1.2);
      }
    }
  `;

  const pulseAnimation = 'pulseOnHover 1.5s infinite ease-in-out';

  return (
    <MapContainer ref={mapContainerRef}>
      <Global styles={globalStyles} />
      <MapFilters
        selectedCountry={selectedCountry}
        query={query}
        setSelectedCountry={setSelectedCountry}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        lang={lang}
        filterMode={filterMode}
        activateFilterMode={activateFilterMode}
        activateTimelineMode={activateTimelineMode}
        allProjects={allProjects}
      />
      <div style={{
        opacity: filterMode === 'timeline' ? 1 : 0,
        visibility: filterMode === 'timeline' ? 'visible' : 'hidden',
        pointerEvents: filterMode === 'timeline' ? 'auto' : 'none',
        transition: 'opacity 0.3s ease',
      }}>
        <Timeline
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          projects={allProjects}
          onAnimationComplete={handleTimelineComplete}
          onInitialAnimationComplete={handleTimelineInitialStepsComplete}
          skipInitialAnimation={initialAnimationDone}
        />
      </div>
      <MapScrollContainer
        ref={mapScrollRef}
        style={{
          height: isMobile ? '500px' : 'auto',
          overflowX: isMobile ? 'auto' : 'visible',
          overflowY: isMobile ? 'hidden' : 'visible',
          opacity: mapVisible ? 1 : 0,
          transition: `opacity ${MAP_ANIMATION.MAP_FADE_DURATION}ms ease-out`,
        }}
      >
        <ComposableMap
          width={isMobile ? 800 : 950}
          height={isMobile ? 500 : 450}
          projection="geoEqualEarth"
          projectionConfig={{
            scale: isMobile ? 160 : 170,
            center: [20, 0],
            rotate: [-10, 0, 0],
          }}
          style={{
            position: 'relative',
            left: isMobile ? '0' : '50%',
            width: isMobile ? '200%' : '120%',
            minWidth: isMobile ? '920px' : 'auto',
            background: 'white',
            marginTop: isMobile ? '0' : '20px',
            marginBottom: isMobile ? '50px' : '80px',
            transform: isMobile ? 'none' : 'translateX(-50%) scale(1.05)',
            fill: theme.colors.greyLight,
            stroke: theme.colors.black,
          }}
        >
        <Geographies geography={geoUrl}>
          {({ geographies }) => {
            return geographies
              .filter((geo) => geo?.properties?.CONTINENT !== 'Antarctica')
              .map((geo) => {
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              });
          }}
        </Geographies>
        {/* Render exiting markers with fade-out animation */}
        {pinsVisible && exitingMarkers.map(({ name, coordinates }) => {
          const pinScale = basePinScale;
          const pinWidth = 24 * pinScale;
          const pinHeight = 32 * pinScale;
          return (
            <Marker key={`exiting-${name}`} coordinates={coordinates}>
              <g
                style={{
                  cursor: 'pointer',
                  animation: `fadeOutPin ${ANIMATION_DURATION}ms ease-out forwards`,
                }}
              >
                <PinContainer>
                  <g transform={`translate(${-pinWidth / 2}, ${-pinHeight})`}>
                    <PinShape d={PIN_PATH} transform={`scale(${pinScale})`} />
                  </g>
                </PinContainer>
              </g>
            </Marker>
          );
        })}
        {pinsVisible && markers.map(({ name, coordinates }, index) => {
          const projectCount = projectCountsByCountry[name] || 0;
          const isHovered = hoveredMarker === name;
          const isActive = activeMarker === name;
          const animatingIndex = animatingMarkers.indexOf(name);
          const isAnimating = animatingIndex !== -1;
          // During initial year-stepping: no stagger (all pins for year appear at once)
          // After initial animation: use sequential stagger for user clicks
          const isIntroAnimation = !timelineInitialStepsDone;
          const staggerDelay = isIntroAnimation 
            ? 0  // No stagger during intro - all pins for each year appear together
            : (isAnimating ? animatingIndex * STAGGER_DELAY : 0);
          // Fade other pins when one is hovered or InfoBox is open
          const shouldFade = (hoveredMarker || activeMarker) && !isHovered && !isActive;
          const markerOpacity = shouldFade ? 0.3 : 1;
          // Use bigger scale for hovered/active pins
          const pinScale = (isHovered || isActive) ? hoveredPinScale : basePinScale;
          const pinWidth = 24 * pinScale;
          const pinHeight = 32 * pinScale;
          // Determine if this pin should animate
          const shouldAnimate = isIntroAnimation || isAnimating;
          return (
            <Marker
              key={name}
              coordinates={coordinates}
              onClick={(e) => handleMarkerClick(name, e)}
              onMouseEnter={() => setHoveredMarker(name)}
              onMouseLeave={() => setHoveredMarker(null)}
            >
              <g 
                style={{ 
                  cursor: 'pointer',
                  opacity: markerOpacity,
                  transition: `all ${ANIMATION_DURATION}ms ease`,
                  animation: shouldAnimate ? `fadeInPin ${ANIMATION_DURATION}ms ease-out ${staggerDelay}ms both` : 'none',
                }}
              >
                {/* Country label above pin - only show on hover */}
                {(isHovered || isActive) && (
                  <CountryLabel
                    y={-pinHeight - 15}
                    style={{ fontSize: isMobile ? 12 : 10 }}
                  >
                    {name}
                  </CountryLabel>
                )}
                {/* Pin shape */}
                <PinContainer style={{
                  animation: (isHovered || isActive) ? pulseAnimation : 'none',
                }}>
                  <g transform={`translate(${-pinWidth / 2}, ${-pinHeight})`}>
                    <PinShape
                      d={PIN_PATH}
                      transform={`scale(${pinScale})`}
                    />
                    {/* Show count on hover or when InfoBox is open, if more than 1 project */}
                    {(isHovered || isActive) && (
                      <PinCount
                        x={pinWidth / 2}
                        y={pinHeight * 0.4}
                        style={{ fontSize: isMobile ? 12 : 10 }}
                      >
                        {projectCount}
                      </PinCount>
                    )}
                  </g>
                </PinContainer>
              </g>
            </Marker>
          );
        })}
      </ComposableMap>
      
      </MapScrollContainer>
      {/* Scroll hint indicator - mobile only */}
      {isMobile && showScrollHint && mapVisible && (
        <div
          ref={scrollHintRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            height: '100%',
            width: '100%',
            pointerEvents: 'none',
            opacity: pinsVisible ? 1 : 0,
            transition: 'opacity 0.3s ease',
            background: 'rgba(255,255,255,0.25)',
          }}
        >
          <img
            src="https://imgvisuals.com/cdn/shop/products/animated-horizontal-move-linear-icon-729834.gif?v=1697532482"
            alt="Scroll horizontally"
            style={{
              filter: 'contrast(200%) brightness(80%);',
              width: '250px',
              opacity: 0.15,
              height: 'auto',
              position: 'absolute',
              bottom: '10%',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      )}

      {/* InfoBox popup */}
      {activeMarker && (
        <div data-infobox>
          <InfoBox
            country={activeMarker}
            projects={getProjectsForCountry(activeMarker)}
            onClose={handleCloseInfoBox}
            style={{
              position: 'absolute',
              ...(infoBoxPosition.x === 'center' 
                ? { left: '50%', transform: 'translateX(-50%)' }
                : { left: infoBoxPosition.x }),
              top: infoBoxPosition.y,
            }}
          />
        </div>
      )}
    </MapContainer>
  );
};

WorldMap.propTypes = {
  selectedCountry: PropTypes.any,
  query: PropTypes.object.isRequired,
  setSelectedCountry: PropTypes.func.isRequired,
  setSelectedService: PropTypes.func.isRequired,
  selectedService: PropTypes.string,
  allProjectsJson: PropTypes.array.isRequired,
  allProjects: PropTypes.array.isRequired,
  lang: PropTypes.object,
  selectedYear: PropTypes.number,
  setSelectedYear: PropTypes.func.isRequired,
  filterMode: PropTypes.string,
  activateFilterMode: PropTypes.func,
  activateTimelineMode: PropTypes.func,
};

export default WorldMap;
